import { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./AuthProvider";
import { getAppSettings } from "../helpers/http_settings_requests";
import { getMetals } from "../helpers/http_metals_requests";
import { getProductionPresets } from "../helpers/http_production_presets_requests";
import { buildDefaultDeliveryDate, metalList as fallbackMetalList, metalNames as fallbackMetalNames, materials as fallbackMaterials } from "../utils/data_variables";

const CatalogContext = createContext({});

export function CatalogProvider({ children }) {
    const { id } = useAuth();
    const enabled = Boolean(id);

    const settingsQuery = useQuery({
        queryKey: ["getAppSettings"],
        queryFn: getAppSettings,
        staleTime: 1000 * 60,
        enabled,
    });
    const metalsQuery = useQuery({
        queryKey: ["getMetals"],
        queryFn: getMetals,
        staleTime: 1000 * 60,
        enabled,
    });
    const presetsQuery = useQuery({
        queryKey: ["getProductionPresets"],
        queryFn: getProductionPresets,
        staleTime: 1000 * 60,
        enabled,
    });

    const settings = settingsQuery.data?.data?.data ?? {};
    const metals = Array.isArray(metalsQuery.data?.data?.data) ? metalsQuery.data.data.data : [];
    const presets = Array.isArray(presetsQuery.data?.data?.data) ? presetsQuery.data.data.data : [];

    const activeMetals = metals.filter((metal) => metal.is_active);
    const metalList = activeMetals.length
        ? activeMetals.map((metal) => ({ value: metal.id, label: metal.name }))
        : fallbackMetalList;
    const metalNames = activeMetals.length
        ? Object.fromEntries(activeMetals.map((metal) => [metal.id, metal.name]))
        : fallbackMetalNames;
    const materials = activeMetals.length
        ? Object.fromEntries(activeMetals.map((metal) => [metal.id, { name: metal.name, color: metal.color }]))
        : fallbackMaterials;

    const workdays = String(settings.workdays || "1,2,3,4,5")
        .split(",")
        .map((day) => Number(day.trim()))
        .filter((day) => day >= 0 && day <= 6);
    const defaultDelivery = useMemo(
        () => buildDefaultDeliveryDate(settings.delivery_business_days || 3, workdays),
        [settings.delivery_business_days, settings.workdays]
    );

    const value = {
        settings,
        metals,
        presets: presets.filter((preset) => preset.is_active),
        metalList,
        metalNames,
        materials,
        defaultDeliveryDate: defaultDelivery,
        isLoadingCatalog: settingsQuery.isFetching || metalsQuery.isFetching || presetsQuery.isFetching,
    };

    return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export function useCatalog() {
    return useContext(CatalogContext);
}
